import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { tap } from 'rxjs';
import { HttpService } from '../../../services/http.service';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';

interface StatusTypeResponse {
  status: boolean;
  message: string;
  data: statusData[];
}
interface ApiDataResponse {
  status: boolean;
  message: string;
  data: statusData[];
}

interface statusData {
  description: any;
  isActive: boolean;
  typeName: any;
  eventTypeId: string;
  eventTypeName: string;
  memberId: string;
  fullName: string;
  focusAreaId: string;
  focusAreaName: string;
  audienceTypeId: string;
  audienceTypeName: string;
  eventHostRegionName: string;
  eventHostRegionId: string;
  name: string;
  id: string;
  mccEventPartnerId: string;
  mccEventPartnerName: string;
  status?: string;
}

interface UploadedFile {
  file: File;
  name: string;
  size: string;
  type: 'image' | 'pdf';
  previewUrl: string;
  id: string;
  isExisting?: boolean; // Add this
  existingUrl?: string; // Add this
}
@Component({
  selector: 'app-submit-event',
  standalone: true,
  imports: [CommonModule, MatDialogModule, InputComponent, ReactiveFormsModule],
  templateUrl: './submit-event.component.html',
  styleUrls: ['./submit-event.component.scss'],
})
export class SubmitEventComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  noteText: string = '';
  fileControl = new FormControl('');
  isSubmitting: boolean = false;
  statusOption: any = [];
  workExperinceTypeOption: any = [];
  uploadedFiles: UploadedFile[] = [];
  private objectUrls: string[] = [];
  isDragOver: boolean = false;

  isEditMode: boolean = false;
  eventId: string = '';
  user: any;

  constructor(
    public dialogRef: MatDialogRef<SubmitEventComponent>,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private cdRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private httpService: HttpService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      initialData?: any;
      EditMode?: boolean;
      ViewMode?: boolean;
      schoolID?: string;
      eventId?: string;
      addStudent?: boolean;
    },
  ) {
    this.isEditMode = data?.EditMode || false;
    if (data?.initialData?.eventId) {
      this.eventId = data.initialData.eventId;
    } else if (data?.eventId) {
      this.eventId = data.eventId;
    }
  }

  ngOnInit(): void {
    this.initializeForm();
    this.fetchEventStatus().subscribe();
    this.fetchEventCategoryList().subscribe();
    if (this.isEditMode && this.data?.initialData) {
      this.populateFormForEdit(this.data.initialData);
    }
  }

  ngOnDestroy(): void {
    this.cleanupObjectUrls();
  }

  private populateFormForEdit(eventData: any): void {
    const formatDateForInput = (dateString: string): string => {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    this.form.patchValue({
      eventName: eventData.eventName || 'Work Experience',
      fromDate: formatDateForInput(eventData.eventStartDate),
      toDate: formatDateForInput(eventData.eventEndDate),
      employerName: eventData.employerName || '',
      eventTime: eventData.eventTime || '',
      location: eventData.location || '',
      eventDescription:
        eventData.description || eventData.eventDescription || '',
    });
    if (eventData.workExperienceTypeId) {
      setTimeout(() => {
        const workTypeItem = this.workExperinceTypeOption.find(
          (item: any) => item.value === eventData.workExperienceTypeId,
        );
        if (workTypeItem) {
          this.form.get('workExperinceType')?.setValue(workTypeItem.value);
        }
      }, 500);
    }

    if (eventData.eventImages || eventData.imagePaths) {
      try {
        let imagePaths = [];
        if (
          typeof eventData.eventImages === 'string' ||
          typeof eventData.imagePaths === 'string'
        ) {
          imagePaths = JSON.parse(
            eventData.eventImages || eventData.imagePaths,
          );
        } else if (
          Array.isArray(eventData.eventImages || eventData.imagePaths)
        ) {
          imagePaths = eventData.eventImages || eventData.imagePaths;
        }

        if (imagePaths.length > 0) {
          this.loadExistingImages(imagePaths);
        }
      } catch (e) {
        console.error('Error parsing event images:', e);
        if (
          typeof eventData.eventImages === 'string' &&
          eventData.eventImages.trim() !== ''
        ) {
          this.loadExistingImages([eventData.eventImages]);
        }
      }
    }

    if (eventData.eventStartDate && eventData.eventEndDate) {
      setTimeout(() => {
        this.calculateDays();
      }, 100);
    }
  }

  private loadExistingImages(imagePaths: string[]): void {
    this.uploadedFiles = [];

    imagePaths.forEach((path, index) => {
      if (path) {
        const isPdf = path.toLowerCase().endsWith('.pdf');
        const fileName = path.split('/').pop() || `file-${index}`;

        const uploadedFile: UploadedFile = {
          file: new File([], fileName),
          name: fileName,
          size: '0 MB',
          type: isPdf ? 'pdf' : 'image',
          previewUrl: isPdf ? 'assets/images/pdf-icon.svg' : path,
          id: this.generateFileId(),
          isExisting: true,
          existingUrl: path,
        };

        this.uploadedFiles.push(uploadedFile);
        if (!isPdf && path.startsWith('http')) {
          this.objectUrls.push(path);
        }
      }
    });

    this.cdRef.detectChanges();
  }

  private cleanupObjectUrls(): void {
    this.objectUrls.forEach((url) => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    this.objectUrls = [];
  }

  private noEmptyOrWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === null ||
        control.value === undefined ||
        control.value === ''
      ) {
        return { required: true };
      }
      if (
        typeof control.value === 'string' &&
        control.value.trim().length === 0
      ) {
        return { whitespace: true };
      }

      return null;
    };
  }

  initializeForm(): void {
    this.form = this.fb.group({
      eventName: [{ value: 'Work Experience', disabled: true }],
      fromDate: ['', [Validators.required]],
      toDate: ['', [Validators.required]],
      noOfDays: [''],
      employerName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      eventTime: ['', [this.timeRangeValidator()]],
      location: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      // eventStatus: ['', [Validators.required]],
      workExperinceType: ['', [Validators.required]],
      eventDescription: [''],
    });

    this.form.get('eventTime')?.valueChanges.subscribe(() => {
      this.validateTimeField();
    });
    this.form.get('eventDescription')?.valueChanges.subscribe((value) => {
      this.noteText = value;
    });
  }

  validateTimeField(): void {
    const timeControl = this.form.get('eventTime');
    if (timeControl?.invalid) {
      timeControl.markAsTouched();
      this.cdRef.detectChanges();
    }
  }

  private timeRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value || value === '') {
        return { required: true };
      }

      if (typeof value === 'string') {
        const parts = value.split(' to ');
        if (parts.length !== 2) {
          return { invalidFormat: true };
        }

        const fromTime = parts[0].trim();
        const toTime = parts[1].trim();

        if (!fromTime || fromTime === '') {
          return { missingStart: true };
        }

        if (!toTime || toTime === '') {
          return { missingEnd: true };
        }

        const fromMatch = fromTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        const toMatch = toTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);

        if (fromMatch && toMatch) {
          const fromHours = this.convertTo24HourForComparison(fromMatch);
          const toHours = this.convertTo24HourForComparison(toMatch);
          if (toHours <= fromHours) {
            return { timeRange: true };
          }
        }
      }

      return null;
    };
  }

  private convertTo24HourForComparison(timeMatch: RegExpMatchArray): number {
    let hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const period = timeMatch[3].toUpperCase();

    if (period === 'PM' && hours < 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    this.markFormGroupTouched(this.form);
    this.form.get('eventTime')?.markAsTouched();
    this.validateTimeField();

    if (this.form.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.submitEvent();
    } else {
      this.showValidationErrors();
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
        control?.updateValueAndValidity();
      }
    });
    this.cdRef.detectChanges();
  }

  private showValidationErrors(): void {
    const errors: string[] = [];

    if (this.form.get('fromDate')?.hasError('required')) {
      errors.push('Start date');
    }
    if (this.form.get('toDate')?.hasError('required')) {
      errors.push('End date');
    }
    if (this.form.get('employerName')?.hasError('required')) {
      errors.push('Company/Employer');
    }
    if (this.form.get('eventTime')?.hasError('required')) {
      errors.push('Event time');
    } else if (this.form.get('eventTime')?.hasError('timeRange')) {
      errors.push('Event time (end time must be after start time)');
    } else if (this.form.get('eventTime')?.hasError('invalidFormat')) {
      errors.push('Event time (invalid format)');
    }
    if (this.form.get('location')?.hasError('required')) {
      errors.push('Location');
    }
    if (this.form.get('eventStatus')?.hasError('required')) {
      errors.push('Status');
    }
    if (this.form.get('workExperinceType')?.hasError('required')) {
      errors.push('Type of work experience');
    }

    // if (errors.length > 0) {
    //   this.showErrorToast(`Please fill required fields: ${errors.join(', ')}`);
    // }
  }

  fetchEventStatus() {
    return this.httpService
      .get<StatusTypeResponse>('external-events/event-statuses')
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              this.statusOption = response.data.map((item) => ({
                label: item.name,
                value: item.id,
              }));
              if (this.isEditMode && this.data?.initialData?.eventStatus) {
                const statusItem = this.statusOption.find(
                  (item: any) =>
                    item.label === this.data.initialData.eventStatus,
                );
                if (statusItem) {
                  this.form.get('eventStatus')?.setValue(statusItem.value);
                }
              }
            } else {
              this.showErrorToast(
                'Something went wrong. Please try again later.',
              );
            }
          },
          error: (error) => {
            this.showErrorToast(
              error?.message || 'Something went wrong. Please try again later.',
            );
          },
        }),
      );
  }

  fetchEventCategoryList() {
    return this.httpService
      .get<ApiDataResponse>('settings/workexperiencetype/get_all')
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              if (this.isEditMode) {
                this.workExperinceTypeOption = response.data.map(
                  (item: {
                    typeName: any;
                    id: any;
                    description: any;
                    isActive: boolean;
                  }) => ({
                    label: item.typeName,
                    value: item.id,
                    description: item?.description,
                    isActive: item.isActive,
                  }),
                );
              } else {
                this.workExperinceTypeOption = response.data
                  .filter(
                    (item: { isActive: boolean }) => item.isActive === true,
                  )
                  .map(
                    (item: { typeName: any; id: any; description: any }) => ({
                      label: item.typeName,
                      value: item.id,
                      description: item?.description,
                    }),
                  );
              }

              if (this.isEditMode && this.data?.initialData) {
                let workTypeItem = this.workExperinceTypeOption.find(
                  (item: any) =>
                    item.value === this.data.initialData.workExperienceTypeId,
                );
                if (!workTypeItem && this.data.initialData.eventTypeName) {
                  workTypeItem = this.workExperinceTypeOption.find(
                    (item: any) =>
                      item.label === this.data.initialData.eventTypeName,
                  );
                }

                if (workTypeItem) {
                  this.form
                    .get('workExperinceType')
                    ?.setValue(workTypeItem.value);
                } else if (this.data.initialData.workExperienceTypeId) {                
                  const inactiveItem = {
                    label: this.data.initialData.eventTypeName || 'Other',
                    value: this.data.initialData.workExperienceTypeId,
                    description:
                      this.data.initialData.eventTypeDescription || '',
                    isActive: false,
                  };
                  this.workExperinceTypeOption.unshift(inactiveItem);
                  this.form
                    .get('workExperinceType')
                    ?.setValue(inactiveItem.value);
                }
              }
            } else {
              this.showErrorToast(
                'Something went wrong. Please try again later.',
              );
            }
          },
          error: (err) => {
            console.error('Failed to load event categories:', err);
            this.showErrorToast(err?.message);
            this.workExperinceTypeOption = [];
          },
        }),
      );
  }

  submitEvent(): void {
    this.isSubmitting = true;
    const formData = this.prepareFormData();

    const apiUrl =
      this.isEditMode && this.eventId
        ? `external-events/student-external/update`
        : 'external-events/student-external/create-edit';
    const httpMethod = this.isEditMode ? 'put' : 'post';

    this.httpService[httpMethod](apiUrl, formData, true).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        this.showSuccessToast(response?.message || 'Event saved successfully');
        this.dialogRef.close({ success: true, data: response });
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Error submitting event:', error);
        this.showErrorToast(
          error?.message || 'Something went wrong. Please try again later.',
        );
      },
    });
  }

  private prepareFormData(): FormData {
    const formData = new FormData();
    const formValue = this.form.getRawValue();
    const eventTimeStr = this.formatEventTimeForSubmission(formValue.eventTime);
    const upcomingStatus = this.statusOption?.find(
      (item: any) => item.label?.toLowerCase() === 'upcoming',
    );
    const UpcomingStatusID = upcomingStatus?.value || '';

    if (this.isEditMode && this.eventId) {
      formData.append('eventId', this.eventId);
    }

    formData.append('eventName', 'Work Experience');
    formData.append(
      'eventStartDate',
      this.formatDate(formValue.fromDate) || '',
    );
    formData.append('eventEndDate', this.formatDate(formValue.toDate) || '');
    formData.append('noOfDays', formValue.noOfDays || '0');
    formData.append('eventTime', eventTimeStr);
    formData.append('Location', formValue.location || '');
    formData.append('description', formValue.eventDescription || '');
    formData.append('employerName', formValue.employerName || '');
    formData.append('WorkExperienceTypeId', formValue.workExperinceType || '');
    formData.append('eventStatusId', UpcomingStatusID || '');
    const existingImages = this.uploadedFiles
      .filter((f) => f.isExisting && f.existingUrl)
      .map((f) => f.existingUrl as string);

    const newImages = this.uploadedFiles.filter((f) => !f.isExisting && f.file);
    const removeExistingImages = existingImages.length === 0;
    formData.append('removeExistingImages', removeExistingImages.toString());
    if (existingImages.length > 0) {
      existingImages.forEach((url, index) => {
        formData.append(`existingImagePaths[${index}]`, url);
      });
    } else {
      formData.append('existingImagePaths', '');
    }

    newImages.forEach((file) => {
      if (file.file && file.file.size > 0) {
        formData.append('imageFiles', file.file, file.name);
      }
    });

    return formData;
  }

  private formatEventTimeForSubmission(timeValue: any): string {
    if (!timeValue || typeof timeValue !== 'string') return '';
    return timeValue;
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  autoResize(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (this.uploadedFiles.length >= 1) {
      this.showErrorToast(
        'Only one file can be uploaded. Remove the existing file first.',
      );
      event.target.value = '';
      return;
    }
    if (files && files.length > 0) {
      const file = files[0];
      this.processFile(file);
      event.target.value = '';
    }
  }

  processFile(file: File): void {
    if (this.uploadedFiles.length >= 1) {
      this.showErrorToast(
        'Only one file can be uploaded. Remove the existing file first.',
      );
      return;
    }

    const validTypes = ['application/pdf'];

    if (!validTypes.includes(file.type)) {
      this.showErrorToast('Please select PDF files only');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.showErrorToast(`File "${file.name}" must be less than 5MB`);
      return;
    }

    const fileSize = this.formatFileSize(file.size);
    const isDuplicate = this.uploadedFiles.some(
      (uploadedFile) =>
        uploadedFile.name === file.name && uploadedFile.size === fileSize,
    );

    if (isDuplicate) {
      this.showErrorToast(`File "${file.name}" is already added`);
      return;
    }

    const fileType = file.type.includes('image') ? 'image' : 'pdf';
    const uploadedFile: UploadedFile = {
      file: file,
      name: file.name,
      size: fileSize,
      type: fileType,
      previewUrl: fileType === 'pdf' ? 'assets/images/pdf-icon.svg' : '',
      id: this.generateFileId(),
    };

    this.uploadedFiles = [uploadedFile];

    if (fileType === 'image') {
      this.createImagePreview(uploadedFile);
    } else {
      this.cdRef.detectChanges();
    }
  }

  createImagePreview(uploadedFile: UploadedFile): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const objectUrl = URL.createObjectURL(uploadedFile.file);
      this.objectUrls.push(objectUrl);
      uploadedFile.previewUrl = objectUrl;
      const index = this.uploadedFiles.findIndex(
        (f) => f.id === uploadedFile.id,
      );
      if (index !== -1) {
        this.uploadedFiles[index] = { ...uploadedFile };
        this.uploadedFiles = [...this.uploadedFiles];
        this.cdRef.detectChanges();
      }
    };

    reader.onerror = () => {
      console.error('Error reading file');
      uploadedFile.previewUrl = 'assets/images/image-error.svg';
      this.cdRef.detectChanges();
    };

    reader.readAsArrayBuffer(uploadedFile.file);
  }

  removeImage(index: number): void {
    if (index >= 0 && index < this.uploadedFiles.length) {
      const removedFile = this.uploadedFiles[index];
      if (
        !removedFile.isExisting &&
        removedFile.type === 'image' &&
        removedFile.previewUrl.startsWith('blob:')
      ) {
        URL.revokeObjectURL(removedFile.previewUrl);
        const urlIndex = this.objectUrls.indexOf(removedFile.previewUrl);
        if (urlIndex > -1) {
          this.objectUrls.splice(urlIndex, 1);
        }
      }

      this.uploadedFiles = [];
      this.cdRef.detectChanges();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (this.uploadedFiles.length >= 1) {
      this.showErrorToast(
        'Only one file can be uploaded. Remove the existing file first.',
      );
      return;
    }

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    }
  }

  generateFileId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  calculateDays(): void {
    const fromDate = this.form.get('fromDate')?.value;
    const toDate = this.form.get('toDate')?.value;

    if (fromDate && toDate) {
      const start = new Date(fromDate);
      const end = new Date(toDate);

      if (end < start) {
        this.form.get('noOfDays')?.setValue('');
        this.form.get('toDate')?.setErrors({
          dateRange: 'End date must be on or after the start date',
        });
        return;
      } else {
        this.form.get('toDate')?.setErrors(null);
      }

      let count = 0;
      const current = new Date(start);
      while (current <= end) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++;
        }
        current.setDate(current.getDate() + 1);
      }

      this.form.get('noOfDays')?.setValue(count.toString());
    } else {
      this.form.get('noOfDays')?.setValue('');
    }
  }

  getMinEndDate(): string | null {
    const fromDate = this.form.get('fromDate')?.value;
    if (!fromDate) return null;

    const date = new Date(fromDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
